import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Setup2FA() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [qr, setQr] = useState("");
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  // Guard against React StrictMode double-invocation — each /totp/setup call
  // generates a fresh secret and overwrites the DB, so the QR the user
  // scans wouldn't match the value verify() checks against.
  const requestedRef = useRef(false);

  useEffect(() => {
    if (requestedRef.current) return;
    requestedRef.current = true;
     
    console.info("[Setup2FA] mount → POST /auth/totp/setup");
    api.post("/auth/totp/setup")
      .then((r) => {
         
        console.info("[Setup2FA] setup OK", { qr_len: r.data.qr_data_url?.length, secret_len: r.data.secret?.length });
        setQr(r.data.qr_data_url || "");
        setSecret(r.data.secret || "");
        if (!r.data.qr_data_url) {
          toast.error("Server returned no QR code — please try Log out and back in.");
        }
      })
      .catch(async (e) => {
         
        console.error("[Setup2FA] setup FAILED", {
          status: e.response?.status,
          data: e.response?.data,
          message: e.message,
          code: e.code,
        });
        // Try to pull diagnostic info from the backend for the UI
        let diag = null;
        try {
          const d = await api.get("/auth/diag");
          diag = d.data;
           
          console.info("[Setup2FA] /auth/diag =", diag);
        } catch (_) { /* diag also failed */ }
        const backendMsg = formatApiErrorDetail(e.response?.data?.detail);
        const msg = backendMsg || `${e.code || "ERR"}: ${e.message || "Network error"}`;
        toast.error(msg, { duration: 10000 });
        setQr("");
        setSecret(diag ? `__error__|${msg}|${JSON.stringify(diag).slice(0, 200)}` : `__error__|${msg}`);
        if (e.response?.status === 401 || e.response?.status === 403) {
          setTimeout(() => { logout(); nav("/login"); }, 3000);
        }
      });
     
  }, []);

  const verify = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/totp/verify", { code });
      // Persist the freshly-issued FULL access token, replacing the enroll-only
      // one from login. Otherwise every subsequent /api/* call sends the enroll
      // bearer and returns 403 "This token is enrollment-only" — which manifests
      // as admin pages that "don't load".
      if (r.data?.access_token) localStorage.setItem("access_token", r.data.access_token);
      toast.success("2FA activated");
      await refresh();
      nav("/");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Invalid code");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 sm:p-6"
      style={{ background: "radial-gradient(1200px 800px at 20% 10%, rgba(245,200,66,0.35), transparent), radial-gradient(1000px 700px at 80% 90%, rgba(200,205,220,0.35), transparent)" }}>
      <div className="w-full max-w-lg glass-strong rounded-3xl p-6 sm:p-10 shimmer-border">
        <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full bg-white/70 border border-amber-200 text-xs font-medium text-amber-700">
          <Sparkle size={11} weight="fill" /> Mandatory setup
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mb-2">Enable 2FA</h1>
        <p className="text-sm text-slate-600 mb-6">
          Your admin has made 2FA compulsory. Scan the QR below with Google Authenticator, Authy, or 1Password — then enter the 6-digit code to finish.
        </p>

        {qr ? (
          <div className="flex flex-col items-center gap-4 mb-6">
            <img src={qr} alt="TOTP QR" data-testid="setup-2fa-qr" className="w-52 h-52 rounded-2xl border border-amber-200 bg-white p-2" />
            <div className="text-[10px] font-mono text-slate-500 break-all text-center max-w-full px-4">Secret: {secret}</div>
          </div>
        ) : secret.startsWith("__error__") ? (
          <div className="h-auto flex flex-col items-center justify-center gap-3 text-center p-4 mb-6">
            <div className="text-sm text-red-700 font-semibold">Couldn&apos;t load the QR code.</div>
            <div className="text-xs text-slate-700 font-mono break-all bg-red-50 border border-red-200 rounded-lg p-2 max-h-40 overflow-auto text-left w-full" data-testid="setup-2fa-error">
              {secret.replace(/^__error__\|?/, "")}
            </div>
            <div className="text-xs text-slate-600">
              Check the browser console (F12) for network details, and <span className="font-mono">docker logs paytrack-backend</span> for the backend trace tagged <span className="font-mono">[totp_setup]</span>.
            </div>
            <div className="flex gap-2">
              <Button data-testid="setup-2fa-retry" onClick={() => window.location.reload()} variant="outline" className="rounded-xl h-9">Retry</Button>
              <Button onClick={() => { logout(); nav("/login"); }} variant="outline" className="rounded-xl h-9">Sign out</Button>
            </div>
          </div>
        ) : (
          <div className="h-52 flex items-center justify-center text-sm text-slate-500">Generating QR…</div>
        )}

        <div className="space-y-3">
          <div>
            <Label className="text-xs font-semibold tracking-wide"><ShieldCheck size={13} className="inline mr-1" /> ENTER CODE</Label>
            <Input
              data-testid="setup-2fa-code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="123456"
              inputMode="numeric"
              maxLength={6}
              className="mt-1.5 h-11 rounded-xl bg-white/70 border-white/60 font-mono text-center tracking-widest text-xl"
            />
          </div>
          <Button data-testid="setup-2fa-submit" onClick={verify} disabled={busy || code.length !== 6}
            className="amber-pill w-full h-12 rounded-xl font-semibold">
            {busy ? "Verifying…" : "Verify & finish"}
          </Button>
          <button
            type="button"
            onClick={() => { logout(); nav("/login"); }}
            className="w-full text-xs text-slate-500 hover:text-slate-700 text-center pt-2"
          >
            Sign out
          </button>
        </div>
        <p className="text-[10px] text-slate-500 mt-6 text-center font-mono">Signed in as {user?.email}</p>
      </div>
    </div>
  );
}
